import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Typography, CircularProgress, Chip, ToggleButton, ToggleButtonGroup,
  MenuItem, TextField,
} from '@mui/material';
import PageHeader from '../../ui-components/PageHeader';
import { useMsal } from '@azure/msal-react';
import { authFetch } from '../../utils/authHelpers';
import { leesJson } from '../../utils/apiFout';
import { BACKEND_URL } from '../../utils/apiConfig';
import { formatDateISO } from '../../utils/weekUtils';
import { NIET_PRODUCTIE_SET } from '../../utils/taakStatus';

// Kalenderblokken en afgeronde items zijn geen "todo" (zelfde filter als Weekplan).
const KALENDER_BLOKKEN = NIET_PRODUCTIE_SET;

const PRIO_KLEUR = {
  'Prio 1': { bg: '#fee2e2', fg: '#b91c1c' },
  'Prio 2': { bg: '#ffedd5', fg: '#c2410c' },
  'Prio 3': { bg: '#fef9c3', fg: '#a16207' },
  'Prio 4': { bg: '#fef9c3', fg: '#a16207' },
  Deadline: { bg: '#fee2e2', fg: '#b91c1c' },
  'Wacht op feedback': { bg: '#ffedd5', fg: '#9a3412' },
};
const PRIO_VOLGORDE = ['Prio 1', 'Prio 2', 'Prio 3', 'Prio 4', 'Deadline',
  'Wacht op feedback', 'Geen prio'];

const FONT = '"Inter", system-ui, sans-serif';

const datumKort = (iso) => {
  if (!iso) return '';
  const d = new Date(`${String(iso).slice(0, 10)}T12:00:00`);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleDateString('nl-NL', { day: 'numeric', month: 'short' });
};
const vandaagISO = () => formatDateISO(new Date());

function TaakRij({ taak, toonProject, onOpenProject }) {
  const teLaat = taak.deadline && taak.deadline < vandaagISO();
  const prio = PRIO_KLEUR[taak.prio] || { bg: '#f1f5f9', fg: '#64748b' };
  return (
    <Box sx={{
      display: 'grid', alignItems: 'center', gap: 1, px: 1.5, py: 0.75,
      gridTemplateColumns: { xs: '1fr auto', md: 'minmax(0,1fr) 110px 110px 130px 70px' },
      borderBottom: '1px solid #f1f5f9', bgcolor: '#fff',
      '&:hover': { bgcolor: '#f8fafc' },
    }}>
      <Box sx={{ minWidth: 0 }}>
        <Typography noWrap sx={{ fontFamily: FONT, fontSize: 13.5, fontWeight: 600, color: '#0f172a' }}>
          {taak.naam}
        </Typography>
        <Box sx={{ display: 'flex', gap: 0.75, alignItems: 'center', mt: 0.25, flexWrap: 'wrap' }}>
          {toonProject && taak.projectNaam && (
            <Chip label={taak.projectNaam} size="small" onClick={taak.projectId ? () => onOpenProject(taak.projectId) : undefined}
              sx={{ height: 18, fontSize: 10.5, maxWidth: 320, bgcolor: '#eef2ff', color: '#4338ca', cursor: taak.projectId ? 'pointer' : 'default' }} />
          )}
          {taak.type && <Chip label={taak.type} size="small" sx={{ height: 18, fontSize: 10.5 }} />}
          <Chip label={taak.prio} size="small" sx={{ height: 18, fontSize: 10.5, bgcolor: prio.bg, color: prio.fg, fontWeight: 700 }} />
        </Box>
      </Box>
      <Typography sx={{ fontFamily: FONT, fontSize: 12.5, color: '#64748b', display: { xs: 'none', md: 'block' } }}>
        {datumKort(taak.start)}{taak.eind && taak.eind !== taak.start ? ` – ${datumKort(taak.eind)}` : ''}
      </Typography>
      <Typography sx={{ fontFamily: FONT, fontSize: 12.5, display: { xs: 'none', md: 'block' },
        color: teLaat ? '#b91c1c' : '#64748b', fontWeight: teLaat ? 800 : 400 }}>
        {taak.deadline ? `⚑ ${datumKort(taak.deadline)}` : ''}
      </Typography>
      <Typography noWrap sx={{ fontFamily: FONT, fontSize: 12, color: '#94a3b8', display: { xs: 'none', md: 'block' } }}>
        {taak.opmerking || ''}
      </Typography>
      <Typography sx={{ fontFamily: FONT, fontSize: 12.5, color: '#64748b', textAlign: 'right' }}>
        {taak.uren ? `${taak.uren}u` : ''}
      </Typography>
    </Box>
  );
}

export default function MijnTaken() {
  const navigate = useNavigate();
  const { accounts } = useMsal();
  const userEmail = (accounts[0]?.username || '').toLowerCase();
  const [taken, setTaken] = useState(null);
  const [fout, setFout] = useState(null);
  const [groeperen, setGroeperen] = useState(true);
  const [sortering, setSortering] = useState('start');

  const laad = useCallback(async () => {
    try {
      const r = await authFetch(`${BACKEND_URL}/Api/my-tasks?email=${encodeURIComponent(userEmail)}`);
      const d = await leesJson(r, 'Taken laden');
      setTaken((d.tasks || []).map((rec) => {
        const f = rec.fields || {};
        return {
          id: rec.id,
          naam: f.NaamTaak || 'Naamloos',
          prio: f.PrioNr || 'Geen prio',
          type: f.Type || '',
          start: f.StartDateUitvoerder || '',
          eind: f.EndDateUitvoerder || '',
          deadline: f.Deadline || '',
          uren: parseFloat(f.UrenWerk) || null,
          opmerking: f.Opmerking || '',
          projectNaam: f['Prnr en Naam']?.[0] || null,
          projectId: f.Project?.[0] || null,
        };
      }).filter((t) => !KALENDER_BLOKKEN.has(t.prio) && !KALENDER_BLOKKEN.has(t.type)
        && !/vanallesuurtje|focustijd|dagstart|^lunch$/i.test(t.naam.trim())));
    } catch (e) {
      setFout(e.message || 'Taken laden mislukt.');
    }
  }, [userEmail]);
  useEffect(() => { laad(); }, [laad]);

  const openProject = useCallback((pid) => navigate(`/projects/${pid}`), [navigate]);

  const sorteer = useCallback((lijst) => {
    const sleutel = { start: (t) => t.start || '9999', deadline: (t) => t.deadline || '9999' };
    if (sortering === 'prio') {
      return [...lijst].sort((a, b) => PRIO_VOLGORDE.indexOf(a.prio) - PRIO_VOLGORDE.indexOf(b.prio)
        || String(a.start || '9999').localeCompare(String(b.start || '9999')));
    }
    const f = sleutel[sortering] || sleutel.start;
    return [...lijst].sort((a, b) => String(f(a)).localeCompare(String(f(b))));
  }, [sortering]);

  const groepen = useMemo(() => {
    if (!taken) return [];
    const per = new Map();
    for (const t of taken) {
      const kop = t.projectNaam || 'Zonder project';
      if (!per.has(kop)) per.set(kop, { kop, projectId: t.projectId, taken: [] });
      per.get(kop).taken.push(t);
    }
    return [...per.values()]
      .map((g) => ({ ...g, taken: sorteer(g.taken) }))
      .sort((a, b) => (a.kop === 'Zonder project') - (b.kop === 'Zonder project')
        || b.kop.localeCompare(a.kop, undefined, { numeric: true }));
  }, [taken, sorteer]);

  const plat = useMemo(() => (taken ? sorteer(taken) : []), [taken, sorteer]);
  const totaalUren = useMemo(() => (taken || []).reduce((s, t) => s + (t.uren || 0), 0), [taken]);

  return (
    <Box sx={{ width: '100%', maxWidth: 1800, mx: 'auto', px: { xs: 2, sm: 3, md: 5 }, pt: { xs: 3, md: 4 }, pb: 4 }}>
      <PageHeader
        icon="checklist"
        title="Projecttaken"
        subtitle={taken ? `${taken.length} openstaande taken · ${Math.round(totaalUren)} uur` : 'Al je openstaande taken op één plek'}
        rightActions={(
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
            <ToggleButtonGroup size="small" exclusive value={groeperen ? 'project' : 'plat'}
              onChange={(_e, v) => v && setGroeperen(v === 'project')}>
              <ToggleButton value="project" sx={{ textTransform: 'none', fontWeight: 700, py: 0.25 }}>Per project</ToggleButton>
              <ToggleButton value="plat" sx={{ textTransform: 'none', fontWeight: 700, py: 0.25 }}>Alles</ToggleButton>
            </ToggleButtonGroup>
            <TextField select size="small" value={sortering} onChange={(e) => setSortering(e.target.value)}
              sx={{ minWidth: 150 }} label="Sorteer op">
              <MenuItem value="start">Startdatum</MenuItem>
              <MenuItem value="deadline">Deadline</MenuItem>
              <MenuItem value="prio">Prioriteit</MenuItem>
            </TextField>
          </Box>
        )}
      />

      {fout && <Typography sx={{ fontFamily: FONT, color: '#b91c1c', mt: 2 }}>{fout}</Typography>}
      {!taken && !fout && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}><CircularProgress size={24} /></Box>
      )}

      {taken && taken.length === 0 && (
        <Box sx={{ textAlign: 'center', py: 8 }}>
          <Typography sx={{ fontFamily: FONT, color: '#64748b', fontWeight: 600 }}>
            Geen openstaande taken — lekker bezig 🎉
          </Typography>
        </Box>
      )}

      {taken && taken.length > 0 && (groeperen ? (
        groepen.map((g) => (
          <Box key={g.kop} sx={{ border: '1px solid #e2e8f0', borderRadius: 2, mb: 1.5, overflow: 'hidden' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 1.5, py: 1, bgcolor: '#f8fafc',
              cursor: g.projectId ? 'pointer' : 'default', '&:hover': g.projectId ? { bgcolor: '#f1f5f9' } : {} }}
              onClick={g.projectId ? () => openProject(g.projectId) : undefined}>
              <Typography sx={{ fontFamily: FONT, fontSize: 13.5, fontWeight: 800, color: '#0f172a', flex: 1 }} noWrap>
                {g.kop}
              </Typography>
              <Typography sx={{ fontFamily: FONT, fontSize: 12, color: '#64748b', fontWeight: 700 }}>
                {g.taken.length} taken{g.taken.some((t) => t.uren) ? ` · ${Math.round(g.taken.reduce((s, t) => s + (t.uren || 0), 0))}u` : ''}
              </Typography>
            </Box>
            {g.taken.map((t) => <TaakRij key={t.id} taak={t} toonProject={false} onOpenProject={openProject} />)}
          </Box>
        ))
      ) : (
        <Box sx={{ border: '1px solid #e2e8f0', borderRadius: 2, overflow: 'hidden' }}>
          {plat.map((t) => <TaakRij key={t.id} taak={t} toonProject onOpenProject={openProject} />)}
        </Box>
      ))}
    </Box>
  );
}
